import {
  Button,
  Container,
  Flex,
  LoadingOverlay,
  NumberInput,
  Stack,
  TextInput,
  Textarea,
  Title,
  rem,
  Select,
  Text,
  ActionIcon,
  Checkbox,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconArrowLeft, IconCalendar, IconClock, IconList, IconLogin, IconLogout, IconUser } from '@tabler/icons-react';
import { useState } from 'react';
import { DatePickerInput, TimeInput } from '@mantine/dates';
import axios from 'axios';
import { API_URL } from '../../services/url';
import { CarSeaterList } from './constants';
import { modals } from '@mantine/modals';

export const CustomBooking = () => {
  const isMobile = useMediaQuery("(max-width: 600px)");
  const [loading, setLoading] = useState(false);
  const [validPhonenumber, setValidPhone] = useState(false);
  const [timeValidationError, setTimeValidationError] = useState('');
  const user = localStorage.getItem('user');

  if (!user) {
    window.location.href = (`/user/signin`)
  }

  interface FormDataState {
    email: string;
    contact_number: string;
    numberofguest: number;
    guestname: string;
    pickupDate: Date | null;
    pickupTime: string;
    pickup_address: string;
    pickuploc: string;
    services_required: string | null;
    pickup_postalcode: string;
    drop_address: string;
    droploc: string;
    drop_postalcode: string;
    specialRequests: string;
    driver_code: string;
    repeatBooking: boolean;
    frequency: string;
    selectedDays: string[];
    repeatBookinguntil: Date | null;
  }

  const [formData, setFormData] = useState<FormDataState>({
    email: user ? user : '',
    contact_number: '',
    numberofguest: 1,
    guestname: '',
    pickupDate: null,
    pickupTime: '',
    pickup_address: '',
    pickuploc: '',
    services_required: null,
    pickup_postalcode: '',
    drop_address: '',
    droploc: '',
    drop_postalcode: '',
    specialRequests: '',
    driver_code: '',
    repeatBooking: false,
    frequency: 'daily',
    selectedDays: [],
    repeatBookinguntil: null,
  });

  function goBack(): void {
    window.location.href = "/";
  }

  // Validate time when today is selected
  const validateDateTime = (date: Date | null, time: string) => {
    if (!date || !time) {
      setTimeValidationError('');
      return;
    }

    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();

    if (isToday) {
      const [hours, minutes] = time.split(':').map(Number);
      const selectedTime = new Date();
      selectedTime.setHours(hours, minutes, 0, 0);

      const now = new Date();
      now.setSeconds(0, 0); // Reset seconds and milliseconds for fair comparison

      if (selectedTime <= now) {
        setTimeValidationError('Please select a future time for today\'s date');
      } else {
        setTimeValidationError('');
      }
    } else {
      // For future dates, time is always valid since past dates are blocked by input
      setTimeValidationError('');
    }
  };

  const handleInputChange = (field: string) => (eventOrValue: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | string | null) => {
    const value = typeof eventOrValue === "string" || eventOrValue === null
      ? eventOrValue
      : eventOrValue.currentTarget.value;

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear validation error when user starts typing in pickup time
    if (field === 'pickupTime' && timeValidationError) {
      setTimeValidationError('');
    }
  };

  const handleNumberChange = (field: string) => (value: number | string) => {
    setFormData({
      ...formData,
      [field]: typeof value === 'number' ? value : ''
    });
  };

  const handleDateChange = (field: string) => (value: Date | null) => {
    setFormData({
      ...formData,
      [field]: value
    });

    // Validate date/time when pickup date changes
    if (field === 'pickupDate') {
      validateDateTime(value, formData.pickupTime);
    }
  };

  const handleSelectChange = (field: string) => (value: string | null) => {
    if (value) {
      setFormData({
        ...formData,
        [field]: value
      });
    }
  };

  function mergeDateAndTime(date: Date | null, time: string) {
    if (!date) return null;
    let [hours, minutes] = time.split(":").map(Number);

    return new Date(date.setHours(hours, minutes, 0, 0)).toISOString();
  }

  const logoutmodal = () =>
    modals.openConfirmModal({
      title: "Logout",
      centered: true,
      children: <Text size="sm">Are you sure want to Logout?</Text>,
      labels: {
        confirm: "Logout",
        cancel: "Cancel",
      },
      onCancel: () => console.log("Cancel"),
      onConfirm: () => {
        localStorage.removeItem('user');
        window.location.href = ('/user/signin');
      },
      confirmProps: { color: "red" },
    });

  const isFormValid =
    formData.email &&
    formData.contact_number &&
    formData.numberofguest > 0 &&
    formData.pickupDate &&
    formData.pickupTime &&
    formData.pickup_address &&
    formData.pickuploc &&
    formData.drop_address &&
    formData.droploc &&
    formData.services_required &&
    !validPhonenumber &&
    !timeValidationError;

  const isRepeatBooking =
    formData.email &&
      formData.contact_number &&
      formData.numberofguest > 0 &&
      formData.pickupDate &&
      formData.pickupTime &&
      formData.pickup_address &&
      formData.pickuploc &&
      formData.drop_address &&
      formData.droploc &&
      formData.services_required &&
      formData.repeatBookinguntil &&
      formData.frequency &&
      formData.frequency === 'daily' ? formData.selectedDays.length === 0 : formData.selectedDays.length > 0 &&
      !validPhonenumber &&
    !timeValidationError;

  const isValidEmail = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/.test(formData.email);

  const handleSubmit = async () => {
    if (!isValidEmail) {
      alert('Enter valid email address.');
      return;
    }

    if (timeValidationError) {
      alert('Please select a valid pickup time.');
      return;
    }

    if (formData.repeatBooking ? !isRepeatBooking : !isFormValid) {
      alert('Please fill all required fields');
      return;
    }

    setLoading(true);

    // Merge date and time
    const mergedDateTime = mergeDateAndTime(
      formData.pickupDate,
      formData.pickupTime
    );

    const dailyDates = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

    const repeatEndDate = formData.repeatBookinguntil;
    const formattedRepeatEndDate = repeatEndDate
      ? new Date(Date.UTC(repeatEndDate.getFullYear(), repeatEndDate.getMonth(), repeatEndDate.getDate(), repeatEndDate.getHours(), repeatEndDate.getMinutes(), repeatEndDate.getSeconds()))
        .toISOString()
      : null;

    // Create final submission data with merged datetime
    const submissionData = {
      pickup_at: mergedDateTime,
      posted_by_mail: formData.email,
      contact: formData.contact_number,
      pickupLoc: `${formData.pickuploc}, ${formData.pickup_postalcode}`,
      drop_address: formData.drop_address,
      dropLoc: `${formData.droploc}, ${formData.drop_postalcode}`,
      services_required: formData.services_required,
      number_of_guest: formData.numberofguest,
      name: formData.guestname,
      pickup_address: formData.pickup_address,
      additional_notes: formData.specialRequests,
      driver_code: formData.driver_code,
      public: !formData.driver_code,
      repeat_booking: formData.repeatBooking,
      repeat_type: formData.repeatBooking ? formData.frequency : null,
      repeat_days: `${user && formData.repeatBooking && formData.frequency === 'daily' ? dailyDates : formData.selectedDays}`,
      repeat_endat: formattedRepeatEndDate,
      repeat_pickup_time: user && formData.repeatBooking ? `${mergeDateAndTime(formData.pickupDate, formData.pickupTime)?.split('T')[1].slice(0, 8)}` : '',
    };

    try {
      const response = await axios.post(`${API_URL}jobs/jobbooking/`, submissionData)
      console.log(response.data);

      if (response.data.success) {

        setFormData({
          email: user ? user : '',
          contact_number: '',
          numberofguest: 1,
          guestname: '',
          pickupDate: null,
          pickupTime: '',
          pickuploc: '',
          services_required: null,
          drop_address: '',
          drop_postalcode: '',
          pickup_address: '',
          droploc: '',
          pickup_postalcode: '',
          specialRequests: '',
          driver_code: '',
          repeatBooking: false,
          frequency: 'daily',
          selectedDays: [],
          repeatBookinguntil: null,
        });

        // Clear validation error on successful reset
        setTimeValidationError('');

        alert('Job Created Successfully')

        if (user !== null) {
          window.location.href = `/bookings`;
        }

        setLoading(false);
      }
    } catch (error: any) {
      alert(error.response ? error.response.data.error : error.message)
      console.error("erroor", error);
      setLoading(false);
    }
  };

  const validatePhoneNumber = (phone: string) => {
    const re = /^(?:\+?61|0)[2-478]\d{8}$/;
    if (re.test(phone)) {
      setValidPhone(false);
    } else {
      setValidPhone(true);
    }
  };

  return (
    <Container
      size={isMobile ? "xs" : "sm"}
      px="lg"
      py={isMobile ? "md" : "xl"}
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "self-start",
        textAlign: "left",
      }}
      bg={"bg"}
    >
      <LoadingOverlay visible={loading} />

      <Flex w={'100%'} align="center" justify={'space-between'} mb="sm" gap="sm">
        <Flex align="center" gap="sm">
          <IconArrowLeft
            style={{ width: rem(20), height: rem(20) }}
            stroke={1.5}
            onClick={goBack}
            cursor="pointer"
          />
          <Title order={2} fw={700}>
            Ride Booking
          </Title>
        </Flex>

        <Flex gap="sm">
          <ActionIcon
            variant="filled"
            aria-label="Bookings"
            size="lg"
            radius="xl"
            color="primary"
            onClick={() => (window.location.href = "/Bookings")}
            style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <IconList style={{ width: "70%", height: "70%" }} stroke={1.5} />
          </ActionIcon>

          <ActionIcon
            variant="filled"
            aria-label="Logout"
            size="lg"
            radius="xl"
            color="primary"
            onClick={logoutmodal}
            style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <IconLogout style={{ width: "70%", height: "70%" }} stroke={1.5} />
          </ActionIcon>
        </Flex>
      </Flex>

      <Stack w={'100%'} gap="md" mb="sm">

        <TextInput
          label="Email ID"
          placeholder="ex: myname@example.com"
          required
          size="sm"
          value={formData.email}
          onChange={handleInputChange('email')}
        />

        <TextInput
          label="Customer Contact Number"
          placeholder="Eg : +6141 234 5678 or 0412 345 678"
          required
          type="tel"
          size="sm"
          labelProps={{ style: { alignSelf: "flex-start" } }}
          value={formData.contact_number}
          onChange={handleInputChange('contact_number')}
          error={validPhonenumber ? "Invalid Phone Number" : null}
          onBlur={() => validatePhoneNumber(formData.contact_number)}
        />
        <Text color="gray" size="sm">
          Eg : +6141 234 5678 or 0412 345 678
        </Text>

        <NumberInput
          label="Number of Guests"
          placeholder="Minimum 1 Guests"
          required
          size="sm"
          min={0}
          max={50}
          value={formData.numberofguest}
          onChange={handleNumberChange('numberofguest')}
          error={formData.numberofguest === 0 ? 'number of customer shoulb be 1' : formData.numberofguest > 50 && 'maximum 50 customer'}
        />

        <TextInput
          label="Names of guests to be picked up"
          placeholder="Enter guest names"
          size="sm"
          value={formData.guestname}
          onChange={handleInputChange('guestname')}
        />


        <Flex gap="md" direction={isMobile ? "column" : "row"} style={{ width: "100%" }}>
          <DatePickerInput
            label="Pick Up Date"
            placeholder="MM-DD-YYYY"
            required
            size="sm"
            style={{ flex: 1 }}
            leftSection={<IconCalendar size={16} />}
            value={formData.pickupDate}
            onChange={handleDateChange('pickupDate')}
            minDate={new Date()}
          />
          <TimeInput
            label="Pick Up Time"
            placeholder="HH:MM"
            required
            size="sm"
            leftSection={<IconClock size={16} />}
            style={{ flex: 1 }}
            value={formData.pickupTime}
            onChange={handleInputChange('pickupTime')}
            onBlur={() => validateDateTime(formData.pickupDate, formData.pickupTime)}
            error={timeValidationError}
          />
        </Flex>

        <Checkbox
          label="Repeat booking"
          size="sm"
          checked={formData.repeatBooking}
          onChange={(event) => setFormData({ ...formData, repeatBooking: event.currentTarget.checked })}
        />

        {formData.repeatBooking && (
          <>
            <Select
              label="Repeat Frequency"
              placeholder="Select frequency"
              required
              size="sm"
              data={[
                { label: 'Daily', value: 'daily' },
                { label: 'Custom', value: 'custom' }
              ]}
              value={formData.frequency}
              onChange={handleSelectChange('frequency')}
            />

            {formData.frequency === 'custom' && (
              <Flex direction={isMobile ? "column" : "row"} gap="md" style={{ width: "100%" }}>
                <Text size="sm" c="dimmed">Select days of the week:</Text>
                <Flex gap="xs" style={{ overflow: 'auto' }}>
                  {[{ label: 'Mon', value: 'monday' },
                  { label: 'Tue', value: 'tuesday' },
                  { label: 'Wed', value: 'wednesday' },
                  { label: 'Thu', value: 'thursday' },
                  { label: 'Fri', value: 'friday' },
                  { label: 'Sat', value: 'saturday' },
                  { label: 'Sun', value: 'sunday' }
                  ].map((day, index) => (
                    <Checkbox
                      key={index}
                      label={day.label}
                      size="sm"
                      checked={formData.selectedDays.includes(day.value)}
                      onChange={() => {
                        formData.selectedDays.includes(day.value)
                          ? setFormData({ ...formData, selectedDays: formData.selectedDays.filter((d) => d !== day.value) })
                          : setFormData({ ...formData, selectedDays: [...formData.selectedDays, day.value] })
                      }}
                    />
                  ))}
                </Flex>
              </Flex>
            )}

            <DatePickerInput
              label="Booking Until"
              placeholder="MM-DD-YYYY"
              required
              size="sm"
              leftSection={<IconCalendar size={16} />}
              value={formData.repeatBookinguntil}
              onChange={handleDateChange('repeatBookinguntil')}
              minDate={
                formData.pickupDate
                  ? new Date(new Date(formData.pickupDate).setDate(new Date(formData.pickupDate).getDate() + 1))
                  : undefined
              }
              maxDate={
                formData.pickupDate
                  ? new Date(new Date(formData.pickupDate).setFullYear(new Date(formData.pickupDate).getFullYear() + 1))
                  : undefined
              }
              disabled={!formData.pickupDate}
            />
            {!formData.pickupDate && <Text c="red" size="xs">
              Please select a pickup date first to enable this field.
            </Text>}

          </>
        )}

        {/* Pick up location (kabbie2) */}
        <Title order={4}>Pick Up Location</Title>

        <TextInput
          label="Pickup Address"
          placeholder="Flat / House No / Floor / Building"
          required
          size="sm"
          value={formData.pickup_address}
          labelProps={{ style: { alignSelf: "flex-start" } }}
          onChange={handleInputChange('pickup_address')}
        />

        <TextInput
          label="Pickup Location"
          placeholder="Suburb"
          required
          size="sm"
          labelProps={{ style: { alignSelf: "flex-start" } }}
          value={formData.pickuploc}
          onChange={handleInputChange('pickuploc')}
        />

        <Select
          label="Service Required"
          placeholder="Select service"
          required
          size="sm"
          labelProps={{ style: { alignSelf: "flex-start" } }}
          data={CarSeaterList}
          value={formData.services_required}
          onChange={handleInputChange('services_required')}
        />

        <TextInput
          label="Postal / Zip Code"
          placeholder="Enter postal code"
          size="sm"
          value={formData.pickup_postalcode}
          onChange={handleInputChange('pickup_postalcode')}
        />

        {/* Destination (kabbie1) */}
        <Title order={4}>Destination</Title>

        <TextInput
          label="Drop Address"
          placeholder="Flat / House No / Floor / Building"
          required
          size="sm"
          value={formData.drop_address}
          labelProps={{ style: { alignSelf: "flex-start" } }}
          onChange={handleInputChange('drop_address')}
        />

        <TextInput
          label="Drop Location"
          placeholder="Suburb"
          required
          size="sm"
          labelProps={{ style: { alignSelf: "flex-start" } }}
          value={formData.droploc}
          onChange={handleInputChange('droploc')}
        />

        <TextInput
          label="Postal / Zip Code"
          placeholder="Enter postal code"
          size="sm"
          value={formData.drop_postalcode}
          onChange={handleInputChange('drop_postalcode')}
        />

        {/* Special requests */}
        <Title order={4}>Special Requests</Title>

        <Textarea
          placeholder="Enter any special requests"
          size="sm"
          minRows={4}
          autosize
          maxRows={8}
          value={formData.specialRequests}
          onChange={handleInputChange('specialRequests')}
        />

        <TextInput
          label="Driver Code"
          placeholder="ex: JOHN12"
          size="sm"
          value={formData.driver_code}
          onChange={handleInputChange('driver_code')}
        />
      </Stack>

      <Flex justify="center" w={'100%'}>
        <Button
          w='90%'
          size="lg"
          radius="md"
          variant="gradient"
          gradient={{ from: "#2B2B2B", to: "#404040", deg: 180 }}
          disabled={formData.repeatBooking ? !isRepeatBooking : !isFormValid || loading}
          onClick={handleSubmit}
          loading={loading}
          loaderProps={{ type: "oval" }}
        >
          Submit
        </Button>
      </Flex>

    </Container>
  );
};